import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dropdown, Title } from '../../components/shared';
import MarkersMap from '../../components/shared/map/MarkersMap';
import useMenuHandler from '../../lib/useMenuHandler';
import {
  IMusselLineResource,
  IOysterLineResource,
} from '../../entities/farms.entities';
import { IBoatResource, IBoatTrack } from '../../entities/boat.entities';
import { sendSingleRequest } from '../../apis';
import { formatNumber, hashColor } from '../../entities/util-functions';
import boat_png from '../../images/boat.png';
import { selectAccount } from '../../store/auth/auth.selector';
import { selectFarmsData } from '../../store/farms/farms.selector';
import {
  calcLineLastSize,
  calcSpecieLineDozens,
  getEstHarvestDate,
  getLineColor,
  getLineOysters,
  getLineStatus,
} from '../../lib/farm.helpers';
import { ILocation } from '../../entities/general.entities';
import moment from 'moment';
import {
  defaultDateFormat,
  displayValueUnit,
  getCenterOfPoints,
  toMillisecond,
  toSecond,
} from '../../lib/common.helpers';
import { IMapMarker, IMapSeedPoint, TLang } from '../../entities/ui.entities';
import { translate } from '../../lib/lang.helper';
import { selectLang, selectMapType } from '../../store/ui/ui.selector';
import { selectAllTags } from '../../store/extra/extra.selector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquare } from '@fortawesome/free-regular-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { saveMapType } from '../../store/ui/ui.actions';
import LinesFilterView, {
  checkLineFilter,
} from '../../components/lines/LinesFilterView';
import { IGrowingSeed } from '../../entities/growing.entities';
import { selectCropSpecies } from '../../store/utils/utils.selector';

library.add(faSquareCheck, faSquare);

interface IMapLine {
  id: number;
  name: string;
  farm_id: number;
  isRaft: boolean;
  points: ILocation[];
  color: string;
  is_urgent: boolean;
  stage: number;
  seedings?: IGrowingSeed[];
  lineCapacity: number;
}

interface ICountOf {
  total: number;
  empty: number;
  ready_harvest: number;
  require_assessment: number;
  catch_spat: number;
  growing_mussel: number;
}

interface IMapFarm {
  id: number;
  name: string;
  farm_number: string;
  bounds?: ILocation[] | null;
  countOf: ICountOf;
  status: boolean;
  onClick?: () => void;
}

type IOptionType =
  | 'ALL'
  | 'REQUIRE_ASSESSMENT'
  | 'READY_HARVEST'
  | 'CATCHING_SPAT';

const defaultZoom = 17;

const statusOptions = (lang: TLang | undefined) => [
  { id: '0', label: translate(lang, 'All'), value: 'ALL' },
  {
    id: '1',
    label: translate(lang, 'Require assessment'),
    value: 'REQUIRE_ASSESSMENT',
  },
  {
    id: '2',
    label: translate(lang, 'Ready to harvest'),
    value: 'READY_HARVEST',
  },
  { id: '3', label: translate(lang, 'Catching spat'), value: 'CATCHING_SPAT' },
];

const toLatLng = (x: any) => ({
  lat: Number(x.latitude),
  lng: Number(x.longitude),
});

const topPosition = (farm: IMapFarm) => {
  if (!farm.bounds || farm.bounds.length <= 0) {
    return { lat: 0, lng: 0 };
  }
  let res = farm.bounds[0];
  for (let i = farm.bounds.length - 1; i > 0; i--) {
    if (
      res.lat < farm.bounds[i].lat ||
      (res.lat === farm.bounds[i].lat && res.lng > farm.bounds[i].lng)
    )
      res = farm.bounds[i];
  }
  return res;
};

function generateSeedingPoints(
  points: ILocation[],
  seedings: IGrowingSeed[] | undefined,
  totalBasketCount: any,
): IMapSeedPoint[] {
  if (!seedings || seedings.length === 0) {
    return [{ start: points[0], end: points[1] }];
  }
  const calcPoint = (position: number) => {
    const ratio = (position - 1) / (totalBasketCount - 1);
    return {
      lat: parseFloat(
        (points[0].lat + (points[1].lat - points[0].lat) * ratio).toFixed(10),
      ),
      lng: parseFloat(
        (points[0].lng + (points[1].lng - points[0].lng) * ratio).toFixed(10),
      ),
    };
  };
  const boolFromStart = (
    points: ILocation[],
    direction: string | null | undefined,
  ) => {
    if (!direction) {
      return true;
    }
    if (points[0].lng <= points[1].lng) {
      return direction === 'W' || direction === 'S';
    } else {
      return direction === 'E' || direction === 'N';
    }
  };
  const getRange = (
    positions: Array<{ start_position: number; end_position: number }>,
    size: number,
    isFromStart: boolean,
  ) => {
    positions.sort((a, b) => a.start_position - b.start_position);
    if (isFromStart) {
      let i = 0,
        startPosition = 1;
      for (i = 0; i < positions.length; i++) {
        if (startPosition + size - 1 < positions[i].start_position) {
          break;
        }
        startPosition = positions[i].end_position + 1;
      }
      const endPosition = startPosition + size - 1;
      const startPoint = calcPoint(startPosition);
      const endPoint = calcPoint(endPosition + 1);
      return { startPosition, endPosition, startPoint, endPoint };
    } else {
      let i = positions.length - 1,
        endPosition = totalBasketCount;
      for (i = positions.length - 1; i >= 0; i--) {
        if (endPosition - size + 1 > positions[i].end_position) {
          break;
        }
        endPosition = positions[i].start_position - 1;
      }
      const startPosition = endPosition - size + 1;
      const startPoint = calcPoint(startPosition);
      const endPoint = calcPoint(endPosition + 1);
      return { startPosition, endPosition, startPoint, endPoint };
    }
  };
  seedings.sort((a, b) => {
    if (a.start_position && b.start_position) {
      return a.start_position - b.start_position;
    }
    if (a.start_position) return -1;
    if (b.start_position) return 1;
    return a.id - b.id;
  });
  let result: IMapSeedPoint[] = [];
  for (let i = 0; i < seedings.length; i++) {
    let { start_position, end_position, line_length, basket_count, direction } =
      seedings[i];
    let data_count = line_length ?? basket_count ?? 0;

    const positions = result
      .filter(x => x.seed_data)
      .map(x => ({
        start_position: x.seed_data?.start_position ?? 0,
        end_position: x.seed_data?.end_position ?? 0,
      }));
    if (!start_position || !end_position) {
      const isFromStart = boolFromStart(points, direction);
      const range = getRange(positions, data_count, isFromStart);
      result.push({
        start: range.startPoint,
        end: range.endPoint,
        seed_data: {
          ...seedings[i],
          start_position: range.startPosition,
          end_position: range.endPosition,
        },
      });
    } else {
      const startPoint = calcPoint(start_position);
      const endPoint = calcPoint(end_position + 1);
      result.push({
        start: startPoint,
        end: endPoint,
        seed_data: seedings[i],
      });
    }
  }
  const positions = result
    .filter(x => x.seed_data)
    .map(x => ({
      start_position: x.seed_data?.start_position ?? 0,
      end_position: x.seed_data?.end_position ?? 0,
    }));
  positions.sort((a, b) => a.start_position - b.start_position);
  let startPosition = 1;
  for (let i = 0; i < positions.length; i++) {
    if (startPosition < positions[i].start_position) {
      const startPoint = calcPoint(startPosition);
      const endPoint = calcPoint(positions[i].start_position);
      result.push({
        start: startPoint,
        end: endPoint,
      });
    }
    startPosition = positions[i].end_position + 1;
  }
  if (startPosition <= totalBasketCount) {
    const startPoint = calcPoint(startPosition);
    const endPoint = calcPoint(totalBasketCount + 1);
    result.push({
      start: startPoint,
      end: endPoint,
    });
  }
  return result;
}

const FarmMarker: React.FC<{ farm: IMapFarm; lang: TLang | undefined }> = ({
  farm,
  lang,
}) => (
  <div className='farm-marker'>
    <div className='--header'>
      <span className='--name'>{farm.name}</span>
      <span className='--farm_number'>({farm.farm_number})</span>
    </div>
    {!farm.status && (
      <div className='color-red font-weight-500 text-center'>
        {translate(lang, 'Area Closed')}
      </div>
    )}
    <div className='--footer'>
      <Button
        size={5}
        width='small'
        type='fill'
        color='blue'
        onClick={farm.onClick}
      >
        {translate(lang, 'View')}
      </Button>
    </div>
  </div>
);

const mapOptions = [
  { id: 'roadmap', label: 'Roadmap', value: 'roadmap' },
  { id: 'satellite', label: 'Satellite', value: 'satellite' },
  { id: 'hybrid', label: 'Hybrid', value: 'hybrid' },
  { id: 'terrain', label: 'Terrain', value: 'terrain' },
];

interface IFilter {
  spatSource?: string;
  tagName?: string;
  sizeIndexes: number[];
  taxonomyGroupIds?: number[];
  status: IOptionType;
}

interface ITrackLocation {
  boat: IBoatResource;
  boat_tracks: IBoatTrack[];
}

const FarmsMap = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const allFarms = useSelector(selectFarmsData);
  const lineTags = useSelector(selectAllTags);
  const mapType = useSelector(selectMapType);
  const account = useSelector(selectAccount);
  const unitType = account?.display_unit;
  const onGrowWaste = account?.oyster_crops;
  const oysterSpecies = useSelector(selectCropSpecies);
  const { redirectToFarmVisualLine, redirectToLine } = useMenuHandler();
  const timer = useRef<any>();

  const [center, setCenter] = useState<any>();
  const [boatTracks, setBoatTracks] = useState<ITrackLocation[]>([]);
  const [zoom, setZoom] = useState(defaultZoom);
  const [blurBit, setBlurBit] = useState(0);
  const [filter, setFilter] = useState<IFilter>({
    sizeIndexes: [],
    status: 'ALL',
  });
  const [popupView, setPopupView] = useState<any>();

  const loadLine = (lineId: number, location: ILocation) => {
    try {
      const farm = allFarms.find(f => f.lines.some(l => l.id === lineId));
      if (!farm) return;

      const lnDtl = farm.lines.find(l => l.id === lineId);
      if (!lnDtl) return;

      const lastSeededDate = lnDtl.growing_cycle?.seedings
        ? lnDtl.growing_cycle.seedings.reduce(
            (latest, seed) => {
              return !latest || seed.planned_date_seed > latest
                ? seed.planned_date_seed
                : latest;
            },
            undefined as number | undefined,
          )
        : undefined;
      const musselDtl = lnDtl as IMusselLineResource;
      const oysterDtl = lnDtl as IOysterLineResource;
      const spatSrc = lnDtl.growing_cycle?.main_seed.spat_storage;

      setPopupView({
        position: location,
        onClose: () => setPopupView(undefined),
        view: lnDtl.growing_cycle ? (
          <div
            style={{ marginTop: '-8px', fontSize: '14px', fontWeight: '400' }}
          >
            <strong>{translate(lang, 'Line %s', lnDtl.line_name)}</strong>
            <div>
              {`First seeded on ${defaultDateFormat(
                lnDtl.growing_cycle.main_seed.planned_date_seed,
              )}`}
            </div>
            <div>
              {`Line full date on ${defaultDateFormat(lastSeededDate)}`}
            </div>
            {lnDtl.crop_type === 'MUSSEL' ? (
              <div>
                {`${displayValueUnit(
                  musselDtl.growing_cycle?.current_line_length,
                  unitType,
                  'distance_m',
                )} growing`}
              </div>
            ) : lnDtl.crop_type === 'OYSTER' ? (
              <div>{`${getLineOysters(oysterDtl, 'dozens')} dozens growing`}</div>
            ) : (
              <></>
            )}
            <div>
              {spatSrc ? `Spat source: ${spatSrc.source}` : 'Catch spat'}
            </div>
            {lnDtl.crop_type === 'MUSSEL' ? (
              <div>
                {`${translate(lang, 'Last known size')}: ${displayValueUnit(
                  calcLineLastSize(lnDtl)?.avg,
                  unitType,
                  'size_mm',
                )}`}
              </div>
            ) : lnDtl.crop_type === 'OYSTER' ? (
              <>
                {calcSpecieLineDozens(oysterDtl, onGrowWaste, oysterSpecies)
                  ?.filter(x => x.amount !== undefined && x.amount != 0)
                  ?.map((x, i) => (
                    <div key={i}>
                      {`${x.name}: ${formatNumber(x.amount ?? 0)} dz`}
                    </div>
                  ))}
                <div>
                  {`${translate(lang, 'Total')}: ${getLineOysters(oysterDtl)} dz`}
                </div>
              </>
            ) : (
              <></>
            )}
            <div>
              {`${translate(lang, '_estimated_harvest_date_2')}: ${defaultDateFormat(
                getEstHarvestDate(lnDtl),
              )}`}
            </div>
          </div>
        ) : (
          <div
            style={{ marginTop: '-8px', fontSize: '14px', fontWeight: '400' }}
          >
            {`Line ${lnDtl.line_name} is empty`}
          </div>
        ),
      });
    } catch (e) {
      console.error('loadLine error:', e);
    }
  };

  const { farmsData, linesData, maxStage } = useMemo(() => {
    let farmsData: IMapFarm[] = [],
      linesData: IMapLine[] = [];
    let maxStage = 0;
    for (let farm of allFarms) {
      let tmpFarm: IMapFarm = {
        id: farm.id,
        name: farm.name,
        farm_number: farm.farm_number,
        bounds: farm.bounds,
        countOf: {
          total: 0,
          empty: 0,
          ready_harvest: 0,
          require_assessment: 0,
          catch_spat: 0,
          growing_mussel: 0,
        },
        status: !farm.is_closed,
      };
      for (let line of farm.lines) {
        if (
          line.growing_cycle &&
          line.growing_cycle.main_seed.stage > maxStage
        ) {
          maxStage = line.growing_cycle.main_seed.stage;
        }
        if (!checkLineFilter(line, filter, lineTags)) continue;
        const lineStatus = getLineStatus(
          line,
          account?.line_assess_expire_days,
        );
        if (
          (filter.status === 'CATCHING_SPAT' && lineStatus !== 'CATCH_SPAT') ||
          (filter.status === 'READY_HARVEST' &&
            lineStatus !== 'READY_HARVEST') ||
          (filter.status === 'REQUIRE_ASSESSMENT' &&
            lineStatus !== 'REQUIRE_ASSESSMENT')
        )
          continue;

        let color = getLineColor(
          line,
          account?.stage_labels,
          account?.line_assess_expire_days,
        );

        const tags =
          lineTags?.filter(
            x => !!x.color && x.tag_lines.some(y => y.line_id === line.id),
          ) ?? [];
        if (tags.length > 0 && tags[0].color) {
          color = tags[0].color;
        }

        if (line.points && line.points.length > 1) {
          let lineCapacity = 0;
          if (line.crop_type === 'MUSSEL') {
            const lnD = line as IMusselLineResource;
            lineCapacity = Math.max(
              lnD.backbone,
              lnD.growing_cycle?.current_line_length ?? 0,
            );
          } else if (line.crop_type === 'OYSTER') {
            const lnD = line as IOysterLineResource;
            lineCapacity = Math.max(
              lnD.basket_count,
              lnD.growing_cycle?.current_basket_count ?? 0,
            );
          }
          let tmpLine: IMapLine = {
            id: line.id,
            name: line.line_name,
            farm_id: line.farm_id,
            isRaft: farm.structure === 'RAFT',
            points: line.points,
            color,
            is_urgent: !!line.is_urgent,
            stage: line.growing_cycle?.main_seed.stage ?? 0,
            seedings: line.growing_cycle?.seedings,
            lineCapacity,
          };
          linesData.push(tmpLine);
        }
        if (lineStatus === 'EMPTY') {
          tmpFarm.countOf.empty++;
        } else if (lineStatus === 'READY_HARVEST') {
          tmpFarm.countOf.ready_harvest++;
        } else if (lineStatus === 'REQUIRE_ASSESSMENT') {
          tmpFarm.countOf.require_assessment++;
        } else if (lineStatus === 'CATCH_SPAT') {
          tmpFarm.countOf.catch_spat++;
        } else {
          tmpFarm.countOf.growing_mussel++;
        }
        tmpFarm.countOf.total++;
      }
      tmpFarm.onClick = () => redirectToFarmVisualLine(farm.id);
      farmsData.push(tmpFarm);
    }
    return { farmsData, linesData, maxStage };
  }, [allFarms, lineTags, filter, account]);

  const farmMarkers: IMapMarker[] = farmsData
    .filter(f => f.bounds && f.bounds.length > 0)
    .map(f => ({
      type: 'FARM',
      location: topPosition(f),
      content: <FarmMarker farm={f} lang={lang} />,
      bounds: f.bounds ?? null,
      isClosed: !f.status,
    }));

  const lineMarkers: IMapMarker[] = linesData.map(x => {
    let points = [...(x.points as ILocation[])];
    if (!x.isRaft && points.length === 2 && points[1].lat < points[0].lat) {
      points = [points[1], points[0]];
    }
    const location: ILocation = x.isRaft
      ? getCenterOfPoints(points)
      : points[0];
    const urgent = x.is_urgent ? points[1] : null;
    let color = x.color;
    if (x.stage > 0 && blurBit & (1 << (x.stage - 1))) {
      color = `${color.slice(0, 7)}20`;
    }
    const seedPoints = x.isRaft
      ? undefined
      : x.seedings && x.seedings.length > 0
        ? generateSeedingPoints(points, x.seedings, x.lineCapacity)
        : undefined;

    return {
      type: x.isRaft ? 'RAFT' : 'LINE',
      content: x.name,
      location,
      points,
      seedPoints,
      color,
      onClick: () => redirectToLine(x.farm_id, x.id),
      onMouseOver: p => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => loadLine(x.id, p), 500);
      },
      onMouseOut: () => {
        if (timer.current) clearTimeout(timer.current);
      },
      lineID: x.id,
      urgent,
    };
  });

  const boatMarkers: IMapMarker[] = boatTracks
    .filter(x => x.boat_tracks.length > 0)
    .map(x => ({
      type: 'TRACK',
      location: toLatLng(x.boat_tracks[x.boat_tracks.length - 1]),
      tracks: [toLatLng(x.boat_tracks[x.boat_tracks.length - 1])],
      content: (
        <>
          <div
            style={{
              marginTop: '-10px',
              marginBottom: '5px',
              fontWeight: '500',
            }}
          >
            {moment(
              toMillisecond(x.boat_tracks[x.boat_tracks.length - 1].rec_time),
            ).fromNow()}
          </div>
          <div
            className='--boat-img'
            style={{
              backgroundColor: hashColor(x.boat.reg_number),
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <img src={boat_png} alt='boat' />
          </div>
        </>
      ),
    }));

  const moveCenter = (farm: any) => {
    if (!farm.bounds || farm.bounds.length <= 0) {
      window.alert(translate(lang, '_location_farm_defined'));
    } else {
      setCenter({
        lat: parseFloat(farm.bounds[0].lat),
        lng: parseFloat(farm.bounds[0].lng),
      });
      setZoom(zoom > defaultZoom ? defaultZoom : zoom + 0.01);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await sendSingleRequest(
          { start_time: toSecond(Date.now() - 8 * 3600 * 1000) },
          'GET',
          'api/boat/boat-track',
          true,
        );
        if (res.status) {
          setBoatTracks(Array.isArray(res.data) ? res.data : []);
        }
      } catch (e) {
        console.error('boatTracks load error:', e);
      }
    })();
  }, []);

  return (
    <div className='h-calc-80 bg-secondary'>
      <div className='container pos-relative'>
        <div className='farms-map-page'>
          <div className='farms__header d-flex justify-between align-center'>
            <Title size={5} color='black-3' align='default' fontWeight={700}>
              {translate(lang, 'Farms Map')}
            </Title>
          </div>
          <div className='map-header'>
            <LinesFilterView
              filter={filter}
              onChange={v => setFilter({ ...filter, ...v })}
            />
            <Dropdown
              className='filter-option'
              label=''
              defaultValue={filter.status}
              options={statusOptions(lang)}
              onChange={(v: any) => setFilter({ ...filter, status: v })}
            />
          </div>
          <div className='map-content'>
            <div className='--map'>
              <div className='map-type-div'>
                <Dropdown
                  label=''
                  options={mapOptions}
                  value={mapType ?? 'roadmap'}
                  onChange={v => dispatch(saveMapType(v))}
                />
              </div>
              <MarkersMap
                markers={[...farmMarkers, ...lineMarkers, ...boatMarkers]}
                center={center}
                mapTypeId={mapType as any}
                zoom={zoom}
                popup={popupView}
              />
            </div>
            <div className='--farms'>
              <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
                {farmsData.map(farm => (
                  <div
                    className='--farm-button'
                    key={farm.id}
                    onClick={() => moveCenter(farm)}
                  >
                    <div className='--name-number'>
                      <span style={{ marginRight: '3px' }}>{farm.name}</span>
                      <span style={{ fontSize: '0.8rem' }}>
                        ({farm.farm_number})
                      </span>
                    </div>
                    <div className='--location'>
                      {!!farm.bounds && farm.bounds.length > 0
                        ? `${farm.bounds[0].lat} ${farm.bounds[0].lng}`
                        : '-'}
                    </div>
                    <div className='--lines-count'>
                      <div className='--total-count'>
                        {translate(lang, 'Total %s lines', farm.countOf.total)}
                      </div>
                      <div className='--empty-count'>
                        {translate(lang, '%s lines empty', farm.countOf.empty)}
                      </div>
                      <div className='--ready_harvest-count'>
                        {translate(
                          lang,
                          '_lines_ready_harvest_2',
                          farm.countOf.ready_harvest,
                        )}
                      </div>
                      <div className='--require_assessment-count'>
                        {translate(
                          lang,
                          '_lines_require_assess',
                          farm.countOf.require_assessment,
                        )}
                      </div>
                      <div className='--catch_spat-count'>
                        {translate(
                          lang,
                          '%s lines catch spat',
                          farm.countOf.catch_spat,
                        )}
                      </div>
                      <div className='--growing-count'>
                        {translate(
                          lang,
                          '_lines_normal_growing',
                          farm.countOf.growing_mussel,
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {maxStage > 0 && (
                <div className='--stages'>
                  {[...Array(maxStage).keys()].map(x => (
                    <div
                      key={x}
                      className={
                        x === 0 ? 'cursor-pointer' : 'mt-12 cursor-pointer'
                      }
                      onClick={() => setBlurBit(blurBit ^ (1 << x))}
                    >
                      <FontAwesomeIcon
                        icon={!(blurBit & (1 << x)) ? faSquareCheck : faSquare}
                        color='green'
                        size='lg'
                      />
                      <span className='ml-12'>
                        {translate(lang, 'Stage %s', x + 1)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FarmsMap;
